import React, { useEffect, useReducer, useMemo, useCallback, useState } from 'react';
import styles from '../../../styles/configuration/nlpconfiguration.module.scss';
import Container from './Container';
import { BoltIcon, /* CpuChipIcon, */ ShieldCheckIcon } from '@heroicons/react/24/outline';
import { fetchAutomationModels } from '../../../utils/api';
import { fetchNlpConfiguration, updateNlpConfiguration } from '../../../utils/api/nlp-configuration_apis';
import { STATUS } from '../../../utils/api/sharedfunction';
// import LLMSettings from './LLMSettings';
import SecuritySettings from './SecuritySettings';
import ResourceSettings from './ResourceSettings';
import ButtonComponent from '../../customButton';
import { useSnackbar } from '../../../utils/useSnackbar';
import { trackUiAction } from '../../../utils/telemetryClient';
import { TelemetryActions } from '../../../enums/TelemetryActionsEnum';

const DEFAULT_CONFIGURATION = {
    llm_model_id: '',
    max_tokens: null,
    temperature: 0,
    provider: 'openai',
    model_name: '',
    strict_mode: true,
    semantic_filtering: true,
    llm_validation: false,
    allow_file_downloads: false,
    allow_file_uploads: true,
    allowed_domains: '',
    blocked_domains: '',
    llm_model_id_for_validation: '',
    max_iterations: 25,
    max_steps: 50,
    max_scenario_length: 5000,
};

const configReducer = (state, action) => {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        case 'SET_CONFIG':
            return { ...state, ...action.payload };
        case 'RESET':
            return { ...state, ...action.payload };
        default:
            return state;
    }
};

const NLPConfiguration = () => {
    const [config, dispatch] = useReducer(configReducer, DEFAULT_CONFIGURATION);
    const [originalConfig, dispatchOriginal] = useReducer(configReducer, DEFAULT_CONFIGURATION);
    const [llmModelOptions, setLLMModelOptions] = useState([]);
    const { showSnackbar } = useSnackbar();

    useEffect(() => {
        (async () => {
            const [llmModelsResponse, nlpConfigurationResponse] = await Promise.all([
                fetchAutomationModels(),
                fetchNlpConfiguration()
            ]);
            
            if (llmModelsResponse.status === 1) {
                setLLMModelOptions(llmModelsResponse.data.map(item => ({
                    label: item.name,
                    value: item.id,
                    modelversion: item.modelversion
                })));
            }

            if (nlpConfigurationResponse.status === STATUS.SUCCESS) {
                const data = nlpConfigurationResponse?.data?.configuration || {};
                const mergedConfig = { ...DEFAULT_CONFIGURATION, ...data };
                dispatch({ type: 'SET_CONFIG', payload: mergedConfig });
                dispatchOriginal({ type: 'SET_CONFIG', payload: mergedConfig });
            }
        })();
    }, []);

    const handleInputChange = useCallback((event, field) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        
        if (field === 'llmModel') {
            const selectedModel = llmModelOptions.find(item => item.value === value);
            dispatch({ type: 'SET_CONFIG', payload: { 
                llm_model_id: value, 
                model_name: selectedModel?.modelversion || '' 
            }});
        } else {
            const fieldMap = {
                maxToken: 'max_tokens',
                temperature: 'temperature',
                provider: 'provider',
                strictMode: 'strict_mode',
                semanticFiltering: 'semantic_filtering',
                llmValidation: 'llm_validation',
                allowFileDownloads: 'allow_file_downloads',
                allowFileUploads: 'allow_file_uploads',
                allowedDomains: 'allowed_domains',
                blockedDomains: 'blocked_domains',
                llmModelForValidation: 'llm_model_id_for_validation',
                maxIterations: 'max_iterations',
                maxSteps: 'max_steps',
                maxScenarioLength: 'max_scenario_length'
            };
            dispatch({ type: 'SET_FIELD', field: fieldMap[field], value });
        }
    }, [llmModelOptions]);

    const handleSaveChanges = useCallback(async () => {
        const response = await updateNlpConfiguration({ configuration: config });
        if (response.status === STATUS.SUCCESS) {
            trackUiAction(TelemetryActions.NLP_CONFIG_SAVED);
            dispatchOriginal({ type: 'SET_CONFIG', payload: config });
            showSnackbar(response.message, 'success');
        } else {
            showSnackbar(response.error, 'error');
        }
    }, [config, showSnackbar]);

    const handleResetChanges = useCallback(() => {
        dispatch({ type: 'RESET', payload: originalConfig });
    }, [originalConfig]);

    const configSections = useMemo(() => [
        // {
        //     id: 'llm-settings',
        //     title: '🤖 LLM Settings',
        //     description: 'Configure your language model settings and parameters',
        //     icon: CpuChipIcon,
        //     content: <LLMSettings
        //         llmModelOptions={llmModelOptions}
        //         llmModel={config.llm_model_id}
        //         maxToken={config.max_tokens}
        //         temperature={config.temperature}
        //         provider={config.provider}
        //         modelName={config.model_name}
        //         handleInputChange={handleInputChange}
        //     />
        // },
        {
            id: 'nlp-settings',
            title: '🔒 Security Settings',
            description: 'Configure security levels and domain restrictions',
            icon: ShieldCheckIcon,
            content: <SecuritySettings 
                llmModelOptions={llmModelOptions} 
                llmModelForValidation={config.llm_model_id_for_validation} 
                strictMode={config.strict_mode} 
                semanticFiltering={config.semantic_filtering} 
                llmValidation={config.llm_validation} 
                allowFileDownloads={config.allow_file_downloads} 
                allowFileUploads={config.allow_file_uploads} 
                allowedDomains={config.allowed_domains} 
                blockedDomains={config.blocked_domains} 
                handleInputChange={handleInputChange} 
            />
        },
        {
            id: 'resource-limits',
            title: '⚡ Resource Limits',
            description: 'Set execution limits and audit logging preferences',
            icon: BoltIcon,
            content: <ResourceSettings 
                maxIterations={config.max_iterations} 
                maxSteps={config.max_steps} 
                maxScenarioLength={config.max_scenario_length} 
                handleInputChange={handleInputChange} 
            />
        }
    ], [config, llmModelOptions, handleInputChange]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.buttonContainer}>
                <ButtonComponent variant='secondary' ctaText='Reset Changes' className='bg-slate200 slate900' onClick={handleResetChanges} />
                <ButtonComponent variant='primary' ctaText='Save Changes' className='bg-primary white' onClick={handleSaveChanges} />
            </div>
            {configSections.map((item) => (
                <Container key={item.id} item={item} />
            ))}
        </div>
    );
};

export default NLPConfiguration;